import { COMPUTE_RESOURCE_LIMITS, type ComputeResourceEntitlement } from './compute-resources.ts';

/** Monetary state uses exact signed integer nano-CNY; `balance` is display convenience only. */
export interface AccountBalance {
  balance: number;
  balanceNanoCny: string;
  /** Lifetime actual wallet debits; excludes credits and historical usage estimates. */
  totalDebitedNanoCny: string;
  balanceStatus: 'active' | 'exhausted';
  currency: 'CNY';
  /** Permanent credits, including existing wallets and separately purchased top-ups. */
  permanentBalanceNanoCny?: string;
  /** Unspent, unexpired subscription credits; consumed before permanent credits. */
  subscriptionBalanceNanoCny?: string;
  nextSubscriptionExpiryAt?: string | null;
}
export interface CreditInput { amountCny: string; note?: string; idempotencyKey: string }
export interface BalanceTransaction {
  id: string;
  userId: string;
  kind: 'credit' | 'usage_debit' | 'subscription_expiry';
  deltaNanoCny: string;
  beforeNanoCny: string;
  afterNanoCny: string;
  createdAt: string;
  note: string | null;
  adminId: string | null;
  /** Administrator display name at the moment of the credit. */
  adminName: string | null;
  usageId: string | null;
  subscriptionPeriodId?: string | null;
  expiresAt?: string | null;
}
export interface CreditResult { balance: AccountBalance; transaction: BalanceTransaction; replayed: boolean }
export interface BalanceTransactionsPage { balance: AccountBalance; transactions: BalanceTransaction[]; page: number; pageSize: number; total: number }

/** Public shop information only. Purchasing a code does not itself credit the wallet. */
export interface TopUpSettings {
  storeName: string;
  storeUrl: string | null;
  /** Only dedicated ReadOS products are purchasable; a mixed shop URL is not a tier link. */
  productUrls?: Partial<Record<RechargePackageId, string>>;
  /** Subscription offers have separate exact shop products; recharge links are never reused. */
  subscriptionProductUrls?: Partial<Record<SubscriptionPlanId, string>>;
}
export interface RedeemCodeInput { code: string }
export interface RedeemCodeResult extends CreditResult { subscription?: SubscriptionPeriod; subscriptionStatus?: SubscriptionStatus }
/** User-approved retail tiers. Prices describe the shop offer, not proof of a payment. */
export const RECHARGE_PACKAGES = [
  {id:'cny-5',priceCny:'5',bonusCny:'0',creditCny:'5'},
  {id:'cny-10',priceCny:'10',bonusCny:'0',creditCny:'10'},
  {id:'cny-20',priceCny:'20',bonusCny:'0',creditCny:'20'},
  {id:'cny-50',priceCny:'50',bonusCny:'0',creditCny:'50'},
  {id:'cny-100',priceCny:'100',bonusCny:'10',creditCny:'110'},
  {id:'cny-200',priceCny:'200',bonusCny:'30',creditCny:'230'},
  {id:'cny-500',priceCny:'500',bonusCny:'90',creditCny:'590'},
  {id:'cny-1000',priceCny:'1000',bonusCny:'200',creditCny:'1200'},
] as const;
export type RechargePackageId = typeof RECHARGE_PACKAGES[number]['id'];
/** User-approved monthly offers. An external purchase alone does not grant any entitlement. */
export const SUBSCRIPTION_PLANS = [
  {id:'go',name:'Go',priceCny:'19.9',creditCny:'20',storageLabel:'1 GB',storageLimitBytes:1024**3,durationDays:30,...COMPUTE_RESOURCE_LIMITS.go},
  {id:'basic',name:'Basic',priceCny:'69',creditCny:'99',storageLabel:'5 GB',storageLimitBytes:5*1024**3,durationDays:30,...COMPUTE_RESOURCE_LIMITS.basic},
  {id:'pro',name:'Pro',priceCny:'198',creditCny:'249',storageLabel:'20 GB',storageLimitBytes:20*1024**3,durationDays:30,...COMPUTE_RESOURCE_LIMITS.pro},
] as const;
export type SubscriptionPlanId = typeof SUBSCRIPTION_PLANS[number]['id'];
export interface SubscriptionPeriod {
  id: string;
  planId: SubscriptionPlanId;
  startsAt: string;
  expiresAt: string;
  creditNanoCny: string;
  remainingNanoCny: string;
  storageLimitBytes: number;
}
export interface SubscriptionStatus {
  /** Current account entitlement; a running sandbox may retain memory until restart. */
  compute?: ComputeResourceEntitlement;
  periods: SubscriptionPeriod[];
  /** Effective subscription contribution; administrator/default baseline is preserved separately. */
  storageLimitBytes: number;
  status: 'active' | 'inactive' | 'pending';
}
export interface RedemptionBatchInput {
  amountCny: string;
  /** When present, the server requires amountCny to match the approved tier total. */
  packageId?: RechargePackageId;
  /** Mutually exclusive with packageId; amountCny must match the plan's AI allowance. */
  subscriptionPlanId?: SubscriptionPlanId;
  quantity: number;
  note?: string;
  expiresAt?: string;
}
/** Redemption secrets are never included in list responses. */
export interface RedemptionBatch {
  id: string;
  amountNanoCny: string;
  /** Advertised shop price and bonus; these do not assert that a payment occurred. */
  packageId?: RechargePackageId | null;
  subscriptionPlanId?: SubscriptionPlanId | null;
  storageLimitBytes?: number;
  durationDays?: number;
  priceNanoCny?: string;
  bonusNanoCny?: string;
  quantity: number;
  redeemedCount: number;
  revokedCount: number;
  createdAt: string;
  expiresAt: string | null;
  note: string | null;
  creatorName: string | null;
}
/** Plaintext codes are returned only when a batch is created, for shop inventory import. */
export interface RedemptionBatchCreated { batch: RedemptionBatch; codes: string[] }
export interface RedemptionBatchesPage { batches: RedemptionBatch[]; page: number; pageSize: number; total: number }
